/* Tork SEO — component library + block renderer.
 * Adapted for this app's stack (Vite + React + wouter). Renders the page BODY only;
 * the SEO page wrapper (client/src/pages/seo-page.tsx) supplies the site Header/Footer.
 * Self-contained styles live in client/src/styles/tork-seo.css.
 */
import { useEffect } from "react";
import { Link } from "wouter";
import { TRIAL_HREF, TRIAL_LABEL, RISK_REVERSAL, TRUST_ITEMS, SITE_ORIGIN } from "../../lib/tork/cta";

/* ----------------------------- Types ----------------------------- */
export type Block =
  | { type: "trustBar" }
  | { type: "hero"; h1: string; subhead: string; primaryCta?: string; secondaryCta?: string; riskReversal?: string }
  | { type: "answerBox"; md: string }
  | { type: "prose"; heading?: string; md: string }
  | { type: "table"; caption?: string; headers: string[]; rows: string[][] }
  | { type: "ctaBand"; headline: string; body?: string; ctaLabel?: string; riskReversal?: string }
  | { type: "caseCallout"; md: string }
  | { type: "splitCompare"; leftTitle: string; leftMd: string; rightTitle: string; rightMd: string }
  | { type: "statRow"; items: string[] }
  | { type: "pricingCard"; name: string; trial: string; then: string; includes: string[]; ctaLabel?: string }
  | { type: "valueStack"; heading?: string; items: string[] }
  | { type: "guarantee"; heading: string; md: string }
  | { type: "linkCardGrid"; cards: { title: string; href: string; blurb: string }[] }
  | { type: "rankedList"; items: { rank: number; name: string; badge?: string; tagline: string; bestFor: string; standout: string; price: string; href?: string }[] }
  | { type: "faq"; items: { q: string; a: string }[] }
  | { type: "relatedLinks"; links: { label: string; href: string }[] };

export type Page = {
  slug: string;            // e.g. "/alternatives/merchynt"
  title: string;           // <title>
  description: string;     // meta description
  ogTitle?: string;
  ogDescription?: string;
  breadcrumb?: { name: string; href: string }[]; // visible breadcrumb for /alternatives/*
  jsonLd: object[];        // injected as-is
  blocks: Block[];
};

/* ------------------------- Tiny markdown ------------------------- */
/* Handles: paragraphs, "### " h3, "- " bullets, **bold**, *italic*, `code`, [text](url). */
function inline(text: string, key: number): React.ReactNode {
  const nodes: React.ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0, m: RegExpExecArray | null, i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2]) nodes.push(<strong key={`${key}-${i++}`}>{m[2]}</strong>);
    else if (m[4]) nodes.push(<em key={`${key}-${i++}`}>{m[4]}</em>);
    else if (m[6]) nodes.push(<code key={`${key}-${i++}`} className="tork-code">{m[6]}</code>);
    else if (m[8]) {
      const href = m[9];
      const internal = href.startsWith("/");
      nodes.push(internal
        ? <Link key={`${key}-${i++}`} href={href} className="tork-link">{m[8]}</Link>
        : <a key={`${key}-${i++}`} href={href} className="tork-link" rel="nofollow noopener" target="_blank">{m[8]}</a>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function Md({ md }: { md: string }) {
  const lines = md.replace(/\r/g, "").split("\n");
  const out: React.ReactNode[] = [];
  let para: string[] = [];
  let bullets: string[] = [];
  let k = 0;
  const flushPara = () => { if (para.length) { out.push(<p key={k++} className="tork-p">{inline(para.join(" "), k)}</p>); para = []; } };
  const flushBullets = () => { if (bullets.length) { out.push(<ul key={k++} className="tork-ul">{bullets.map((b, j) => <li key={j}>{inline(b, k * 100 + j)}</li>)}</ul>); bullets = []; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); flushBullets(); continue; }
    if (line.startsWith("### ")) { flushPara(); flushBullets(); out.push(<h3 key={k++} className="tork-h3">{inline(line.slice(4), k)}</h3>); continue; }
    if (line.startsWith("- ")) { flushPara(); bullets.push(line.slice(2)); continue; }
    flushBullets(); para.push(line);
  }
  flushPara(); flushBullets();
  return <>{out}</>;
}

/* --------------------------- Primitives --------------------------- */
function Cta({ label }: { label?: string }) {
  return <a href={TRIAL_HREF} className="tork-btn">{label || TRIAL_LABEL}</a>;
}

/* --------------------------- Block views -------------------------- */
function BlockView({ block }: { block: Block }) {
  switch (block.type) {
    case "trustBar":
      return (
        <div className="tork-trustbar">
          {TRUST_ITEMS.map((t, i) => <span key={i} className="tork-trustbar-item">{t}</span>)}
        </div>
      );
    case "hero":
      return (
        <header className="tork-hero">
          <h1 className="tork-h1">{block.h1}</h1>
          <p className="tork-subhead">{inline(block.subhead, 0)}</p>
          <div className="tork-cta-row">
            <Cta label={block.primaryCta} />
            {block.secondaryCta && <a href="#how-it-works" className="tork-btn-secondary">{block.secondaryCta}</a>}
          </div>
          <p className="tork-risk">{block.riskReversal || RISK_REVERSAL}</p>
        </header>
      );
    case "answerBox":
      return <aside className="tork-answerbox"><Md md={block.md} /></aside>;
    case "prose":
      return <section className="tork-section">{block.heading && <h2 className="tork-h2">{block.heading}</h2>}<Md md={block.md} /></section>;
    case "table":
      return (
        <section className="tork-section">
          {block.caption && <p className="tork-table-caption">{block.caption}</p>}
          <div className="tork-table-wrap">
            <table className="tork-table">
              <thead><tr>{block.headers.map((h, i) => <th key={i}>{inline(h, i)}</th>)}</tr></thead>
              <tbody>{block.rows.map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci} data-label={block.headers[ci]}>{inline(c, ri * 100 + ci)}</td>)}</tr>)}</tbody>
            </table>
          </div>
        </section>
      );
    case "ctaBand":
      return (
        <section className="tork-ctaband">
          <h2 className="tork-h2">{block.headline}</h2>
          {block.body && <p className="tork-p">{inline(block.body, 0)}</p>}
          <Cta label={block.ctaLabel} />
          <p className="tork-risk">{block.riskReversal || RISK_REVERSAL}</p>
        </section>
      );
    case "caseCallout":
      return <aside className="tork-case"><Md md={block.md} /></aside>;
    case "splitCompare":
      return (
        <section className="tork-split">
          <div className="tork-split-col tork-split-bad"><h3 className="tork-h3">{block.leftTitle}</h3><Md md={block.leftMd} /></div>
          <div className="tork-split-col tork-split-good"><h3 className="tork-h3">{block.rightTitle}</h3><Md md={block.rightMd} /></div>
        </section>
      );
    case "statRow":
      return <section className="tork-statrow">{block.items.map((s, i) => <div key={i} className="tork-stat"><Md md={s} /></div>)}</section>;
    case "pricingCard":
      return (
        <section className="tork-pricecard">
          <h2 className="tork-h2">{block.name}</h2>
          <p className="tork-price-trial">{block.trial}</p>
          <p className="tork-price-then">{block.then}</p>
          <ul className="tork-ul">{block.includes.map((it, i) => <li key={i}>{inline(it, i)}</li>)}</ul>
          <Cta label={block.ctaLabel} />
          <p className="tork-risk">{RISK_REVERSAL}</p>
        </section>
      );
    case "valueStack":
      return <section className="tork-section">{block.heading && <h2 className="tork-h2">{block.heading}</h2>}<ol className="tork-ol">{block.items.map((it, i) => <li key={i}>{inline(it, i)}</li>)}</ol></section>;
    case "guarantee":
      return <section className="tork-guarantee"><h2 className="tork-h2">{block.heading}</h2><Md md={block.md} /></section>;
    case "linkCardGrid":
      return (
        <section className="tork-cardgrid">
          {block.cards.map((c, i) => (
            <Link key={i} href={c.href} className="tork-card">
              <span className="tork-card-title">{c.title}</span>
              <span className="tork-card-blurb">{c.blurb}</span>
            </Link>
          ))}
        </section>
      );
    case "rankedList":
      return (
        <section className="tork-ranked">
          {block.items.map((it, i) => (
            <article key={i} className="tork-ranked-item">
              <div className="tork-ranked-rank">{it.rank}</div>
              <div className="tork-ranked-body">
                <h3 className="tork-ranked-name">
                  {it.name}{it.badge && <span className="tork-ranked-badge">{it.badge}</span>}
                </h3>
                <p className="tork-ranked-tagline">{inline(it.tagline, i)}</p>
                <p className="tork-ranked-meta"><strong>Best for:</strong> {inline(it.bestFor, i * 10 + 1)}</p>
                <p className="tork-ranked-meta"><strong>Standout:</strong> {inline(it.standout, i * 10 + 2)}</p>
                <p className="tork-ranked-meta"><strong>Pricing:</strong> {inline(it.price, i * 10 + 3)}</p>
                {it.href && <Link href={it.href} className="tork-link">Learn more →</Link>}
              </div>
            </article>
          ))}
        </section>
      );
    case "faq":
      return (
        <section className="tork-section" id="faq">
          <h2 className="tork-h2">Frequently asked questions</h2>
          <div className="tork-faq">
            {block.items.map((f, i) => (
              <details key={i} className="tork-faq-item">
                <summary className="tork-faq-q">{f.q}</summary>
                <div className="tork-faq-a">{inline(f.a, i)}</div>
              </details>
            ))}
          </div>
        </section>
      );
    case "relatedLinks":
      return (
        <nav className="tork-related" aria-label="Related pages">
          {block.links.map((l, i) => <Link key={i} href={l.href} className="tork-link">{l.label}</Link>)}
        </nav>
      );
    default:
      return null;
  }
}

/* --------------------------- JSON-LD + head --------------------------- */
function JsonLd({ data }: { data: object[] }) {
  return <>{data.map((d, i) => <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(d) }} />)}</>;
}

function StickyMobileCta() {
  return <div className="tork-sticky-cta"><a href={TRIAL_HREF} className="tork-btn">{TRIAL_LABEL}</a></div>;
}

/* The site VSL (same Vimeo video as the homepage hero). Rendered on every SEO
 * page right after the hero so the sales video carries through the funnel.
 * Autoplays muted (browser-allowed); the user can unmute via player controls. */
export function TorkVsl() {
  return (
    <div className="tork-vsl" data-testid="vsl-video-wrapper">
      <div className="tork-vsl-frame" style={{ padding: "56.25% 0 0 0", position: "relative" }}>
        <iframe
          src="https://player.vimeo.com/video/1198390423?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&muted=1&loop=1"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
          title="Local Business Owners: Get Into Google Maps' Top 3 For $1 - Tork Powered By Digital Domination"
          data-testid="vsl-video"
        />
      </div>
    </div>
  );
}

/* Sets a <meta> tag and returns a restore fn that reverts it to its exact prior
 * state on unmount (removing it if it did not previously exist). */
function applyMeta(attr: "name" | "property", key: string, content: string): () => void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  const existed = !!el;
  const prev = el?.getAttribute("content") ?? null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return () => {
    if (!existed) el?.parentNode?.removeChild(el);
    else if (prev !== null) el?.setAttribute("content", prev);
  };
}

/* SPA replacement for Next's makeMetadata: sets <title>, description, canonical
 * and OG tags on mount; fully restores every mutated head tag on unmount so a
 * SEO page never leaks its metadata onto the next route. */
export function useTorkSeoHead(page: Page) {
  useEffect(() => {
    const url = SITE_ORIGIN + page.slug;
    const restores: Array<() => void> = [];

    const prevTitle = document.title;
    document.title = page.title;
    restores.push(() => { document.title = prevTitle; });

    restores.push(applyMeta("name", "description", page.description));
    restores.push(applyMeta("property", "og:title", page.ogTitle || page.title));
    restores.push(applyMeta("property", "og:description", page.ogDescription || page.description));
    restores.push(applyMeta("property", "og:url", url));
    restores.push(applyMeta("property", "og:type", "website"));

    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const linkExisted = !!link;
    const prevHref = link?.getAttribute("href") ?? null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", url);
    restores.push(() => {
      if (!linkExisted) link?.parentNode?.removeChild(link);
      else if (prevHref !== null) link?.setAttribute("href", prevHref);
    });

    return () => { for (const r of restores.reverse()) r(); };
  }, [page]);
}

/* --------------------------- Page assembler --------------------------- */
export function TorkSeoPage({ page }: { page: Page }) {
  return (
    <main className="tork-page" id="how-it-works">
      <JsonLd data={page.jsonLd} />
      {page.breadcrumb && (
        <nav className="tork-breadcrumb" aria-label="Breadcrumb">
          {page.breadcrumb.map((b, i) => (
            <span key={i}>{i > 0 && <span className="tork-bc-sep"> / </span>}<Link href={b.href} className="tork-link">{b.name}</Link></span>
          ))}
        </nav>
      )}
      {page.blocks.flatMap((b, i) => {
        const view = <BlockView key={`block-${i}`} block={b} />;
        // Render the VSL immediately after the hero on every SEO page.
        return b.type === "hero" ? [view, <TorkVsl key={`vsl-${i}`} />] : [view];
      })}
      <StickyMobileCta />
    </main>
  );
}
